import type { ItemType } from '@hero/schema';
import { resolveBayanGoodType } from './bayan.good-types';
import { BAYAN_GOOD_TYPE_SNAPSHOT } from './bayan.good-types.data';

/** A good type as a picker needs it — the API's `BayanGoodTypeView` fits. */
export type BayanGoodTypeNode = {
  id: number;
  nameEnglish: string;
  nameArabic: string;
  parentId?: number;
};

export type BayanGoodTypeOption = BayanGoodTypeNode & {
  /** The name in the reader's language. */
  label: string;
  /** The name in the other language — ELM ships both, staff search either. */
  altLabel: string;
};

/**
 * The submittable leaves of the snapshot, for callers with no live list —
 * staff tools, or the first render before the lookup resolves.
 */
export const BAYAN_GOOD_TYPE_LEAVES: readonly BayanGoodTypeNode[] = [
  ...BAYAN_GOOD_TYPE_SNAPSHOT.values(),
]
  .filter(node => !node.hasChildren)
  .map(({ id, nameEnglish, nameArabic, parentId }) => ({ id, nameEnglish, nameArabic, parentId }));

/**
 * The one way every picker turns Bayan's catalogue into options.
 *
 * Narrowing to an item type's branch is a shortcut, not a filter: a type with
 * no branch, or a branch the list doesn't know, falls back to everything so
 * the user can still find their cargo.
 */
export const listBayanGoodTypeOptions = (args: {
  /** The live list for this Org. Omit to use the snapshot leaves. */
  nodes?: readonly BayanGoodTypeNode[];
  narrowToItemType?: ItemType;
  /**
   * The branch a waybill blocker named. Wins over the item type: it comes
   * from the live tree, so it knows about sub-types the snapshot does not.
   */
  narrowToBranchId?: number;
  language: string;
}): BayanGoodTypeOption[] => {
  const all = args.nodes && args.nodes.length > 0 ? args.nodes : BAYAN_GOOD_TYPE_LEAVES;
  const branchId =
    args.narrowToBranchId ??
    (args.narrowToItemType
      ? resolveBayanGoodType({ itemType: args.narrowToItemType }).branchId
      : undefined);
  const scoped = branchId ? all.filter(node => node.parentId === branchId) : [];
  const source = scoped.length > 0 ? scoped : all;

  const isArabic = args.language.startsWith('ar');
  return source
    .map(node => ({
      ...node,
      label: isArabic ? node.nameArabic : node.nameEnglish,
      altLabel: isArabic ? node.nameEnglish : node.nameArabic,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, isArabic ? 'ar' : 'en'));
};

/** Case-insensitive match on either name. */
export const matchesBayanGoodTypeQuery = (option: BayanGoodTypeOption, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    option.nameArabic.toLowerCase().includes(q) || option.nameEnglish.toLowerCase().includes(q)
  );
};
